import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useWebPubSub } from '../contexts/WebPubSubContext';
import './UserMessages.css';

const UserMessages: React.FC = () => {
  const { toasts, dismissToast, connectionState } = useWebPubSub();
  const [exitPhase, setExitPhase] = useState(false);
  const dismissingIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (toasts.length === 0) {
      setExitPhase(false);
      dismissingIdRef.current = null;
    }
  }, [toasts.length]);

  const handleDismissStart = useCallback(() => {
    const id = toasts[0]?.id;
    if (!id || exitPhase) {
      return;
    }
    dismissingIdRef.current = id;
    setExitPhase(true);
  }, [toasts, exitPhase]);

  const handleItemAnimationEnd = useCallback(
    (e: React.AnimationEvent<HTMLDivElement>) => {
      if (!exitPhase || e.target !== e.currentTarget) {
        return;
      }
      const name = e.animationName;
      const isExitAnim =
        name === 'user-messages-exit' || name === 'user-messages-exit-reduced';
      if (!isExitAnim) {
        return;
      }
      const id = dismissingIdRef.current;
      dismissingIdRef.current = null;
      if (id) {
        dismissToast(id);
      }
      setExitPhase(false);
    },
    [dismissToast, exitPhase]
  );

  if (toasts.length === 0 && connectionState === 'unavailable') {
    return (
      <div className="user-messages user-messages--hint" title="Realtime updates are not configured for this environment.">
        <span className="user-messages__badge">Live</span>
        <span className="user-messages__hint">Off</span>
      </div>
    );
  }

  if (toasts.length === 0) {
    return null;
  }

  const t = toasts[0];

  const itemClass =
    'user-messages__item' + (exitPhase ? ' user-messages__item--exit' : '');

  return (
    <div className="user-messages" role="status" aria-live="polite" aria-busy={exitPhase}>
      <div key={t.id} className={itemClass} onAnimationEnd={handleItemAnimationEnd}>
        <div className="user-messages__body">
          <div className="user-messages__title">{t.title}</div>
          {t.body ? <div className="user-messages__text">{t.body}</div> : null}
        </div>
        <button
          type="button"
          className="user-messages__dismiss"
          onClick={handleDismissStart}
          disabled={exitPhase}
          aria-label="Dismiss"
        >
          ×
        </button>
      </div>
    </div>
  );
};

export default UserMessages;
